import { ActivatedRoute } from '@angular/router';

import {
  AnalyticsEvent,
  Ga4TrackingEventData,
  PAGE_STRUCTURE_ANGULAR,
  RouteData,
  TrackingData,
  TrackingEvent,
  TrackingEventData,
  TrackingPageViewData
} from '@hdms/shared/common-types';

/**
 * Create a dataLayer pageview event by extending the global tracking data (incl. route data).
 *
 * @param virtualPageURL
 * @param trackingData optional
 */
export function createDataLayerPageView(virtualPageURL: string, trackingData?: TrackingData): TrackingPageViewData {
  return {
    ...trackingData,
    event: TrackingEvent.VIRTUAL_PAGEVIEW,
    virtualPageURL
  };
}

/**
 * Create a dataLayer event by combining the AnalyticsEvent data with the global tracking data.
 * Map shorthand properties of AnalyticsEvent to the actual TrackingEventData properties.
 *
 * @param analyticsEvent
 * @param trackingData optional
 */
export function createDataLayerEvent(
  analyticsEvent: AnalyticsEvent | Ga4TrackingEventData<never>,
  trackingData?: TrackingData
): TrackingEventData {
  const event = { ...analyticsEvent };
  const eventData: TrackingEventData = {
    ...trackingData,
    event: event.event || TrackingEvent.DEFAULT
  };

  const propMap = {
    category: 'eventCategory',
    action: 'eventAction',
    label: 'eventLabel',
    value: 'eventValue'
  };

  Object.keys(propMap).forEach(key => {
    if (event.hasOwnProperty(key) && (event[key] !== '' || event[key] !== null)) {
      eventData[propMap[key]] = event[key];
    }
    delete event[key];
  });

  return {
    ...eventData,
    ...event
  };
}

/**
 * Returns applied trackingData, enriched with additional default props
 * @param trackingData
 */

export function getBaseTrackingData(trackingData: TrackingData): TrackingData {
  return {
    ...trackingData,
    pageStructure: PAGE_STRUCTURE_ANGULAR
  };
}

/**
 * Returns an object with conversed boolean data to string type
 * @param data
 */

export function prepareTrackingData(data: TrackingData): any {
  return Object.assign(
    {},
    ...Object.entries(data).map(([k]) =>
      typeof data[k] === 'boolean' || typeof data[k] === 'number' ? { [k]: data[k].toString() } : { [k]: data[k] }
    )
  );
}

/**
 * Get tracking data from current route config
 * @param activatedRoute
 */
export function getRouteData(activatedRoute: ActivatedRoute): RouteData {
  let route = activatedRoute;
  while (route.firstChild) {
    route = route.firstChild;
  }

  return route.snapshot.data || {};
}
